import EventEmitter from 'events';
import debounce from 'lodash.debounce';

import {
    createDataSyncEncoder,
    type DataSyncDecoderHandlers,
    type DataSyncEncoder,
    dataSyncProtocol,
    getSubscriptionId,
} from '@gitbook/hive-core';
import { Logger } from '@gitbook/util-debug';
import { assert } from '@gitbook/util-ts';
import { type WebSocket, type WebSocketMessage, WebSocketState } from '@gitbook/util-websocket';

const logger = Logger('hive:client:V2Socket');

/** Close the socket after 5 minutes of inactivity (no message sent or received). */
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

/** Maximum number of times _doOpen will be retried on failure before giving up. */
const MAX_OPEN_RETRIES = 3;

/** Base delay (ms) between retries; multiplied by the attempt number for linear back-off. */
const OPEN_RETRY_DELAY_MS = 250;
interface HiveV2SocketOptions {
    forceReconnection: () => Promise<void>;
    decoderHandlers: DataSyncDecoderHandlers;
    managerEmitter: EventEmitter;
    onRemove: () => void;
    path: string;
    webSocket: WebSocket;
    ensureWebSocketOpen: () => Promise<void>;
    getNewSubscriptionId: () => Promise<number>;
    /** Returns true if there are active page or reusable-content sockets sharing this branch. */
    hasActiveSiblingPageSockets: () => boolean;
    /**
     * Called when a non-branch socket is reopened after inactivity.
     * Should restart the associated branch socket (same branchId) if it is also inactive.
     */
    restartBranchSocket?: () => Promise<void>;
}

export class HiveV2Socket {
    private emitter: EventEmitter = new EventEmitter();
    private internalState: WebSocketState = WebSocketState.CLOSED;
    public identifier: dataSyncProtocol.IResourceIdentifier;
    private encoder: DataSyncEncoder;
    public subscriptionId: number | undefined;
    private forceReconnection: () => Promise<void>;
    private decoderHandlers: DataSyncDecoderHandlers;
    private managerEmitter: EventEmitter;
    private onRemove: () => void;
    private _webSocket: WebSocket;
    private ensureWebSocketOpen: () => Promise<void>;
    private getNewSubscriptionId: () => Promise<number>;
    private hasActiveSiblingPageSockets: () => boolean;
    private restartBranchSocket?: () => Promise<void>;
    private destroyed: boolean = false;

    /** Cleanup function for the message listener */
    private unlistenMessages?: () => void;

    /** Cleanup function for the WebSocket state listener */
    private unlistenWebSocketState?: () => void;

    /**
     * In-flight promise for the current open() attempt. Any concurrent open()
     * call while this is set returns the same promise, preventing duplicate
     * subscribe frames from being sent on reconnect.
     */
    private openPromise: Promise<void> | undefined;

    /**
     * True if this socket was closed due to inactivity timeout.
     * When inactive, the datasync client treats open() as a no-op.
     */
    private _inactive: boolean = false;

    public get inactive(): boolean {
        return this._inactive;
    }

    /**
     * Debounced function that marks the socket as inactive and closes it after a period of inactivity.
     * Gets reset whenever a message is sent or received.
     * If the underlying Websocket was not open but we sent a message (i.e. a presence update for ex.),
     * we still want to trigger the inactivity timeout as otherwise the underlying Websocket would stay open indefinitely.
     */
    private resetInactivityTimer = debounce((wasClosed = false) => {
        if ((this.destroyed || this.internalState !== WebSocketState.OPEN) && !wasClosed) {
            return;
        }
        // If this is a branch socket and there are still active page/reusable-content
        // sockets for the same branch, restart the timer instead of closing.
        if (
            this.identifier.type === dataSyncProtocol.ResourceType.BRANCH &&
            this.hasActiveSiblingPageSockets()
        ) {
            this.resetInactivityTimer(wasClosed);
            return;
        }
        logger.info('Marking socket as inactive due to inactivity');
        this._inactive = true;
        this.close();
    }, INACTIVITY_TIMEOUT_MS);

    constructor({
        path,
        webSocket,
        forceReconnection,
        decoderHandlers,
        managerEmitter,
        onRemove,
        ensureWebSocketOpen,
        getNewSubscriptionId,
        hasActiveSiblingPageSockets,
        restartBranchSocket,
    }: HiveV2SocketOptions) {
        this.forceReconnection = forceReconnection;
        this.decoderHandlers = decoderHandlers;
        this.managerEmitter = managerEmitter;
        this.onRemove = onRemove;
        this.ensureWebSocketOpen = ensureWebSocketOpen;
        this._webSocket = webSocket;
        this.getNewSubscriptionId = getNewSubscriptionId;
        this.hasActiveSiblingPageSockets = hasActiveSiblingPageSockets;
        this.restartBranchSocket = restartBranchSocket;

        const branchRegex = /\/branches\/(?<branchName>[^/]+)(?:\/|$)/;
        const branchMatch = path.match(branchRegex);
        const branchName = branchMatch?.groups?.branchName ?? undefined;

        const pagesRegex = /\/pages\/(?<pageId>[^/]+)(?:\/|$)/;
        const reucRegex = /\/reusable-contents\/(?<reucId>[^/]+)(?:\/|$)/;

        const pageMatch = path.match(pagesRegex);
        const reucMatch = path.match(reucRegex);

        const pageId = pageMatch?.groups?.pageId ?? undefined;
        const reusableContentId = reucMatch?.groups?.reucId ?? undefined;

        this.identifier = (() => {
            assert(branchName, 'no branch name');

            if (pageId) {
                return {
                    type: dataSyncProtocol.ResourceType.PAGE,
                    id: pageId,
                    branchId: branchName,
                };
            }

            if (reusableContentId) {
                return {
                    type: dataSyncProtocol.ResourceType.REUSABLE_CONTENT,
                    id: reusableContentId,
                    branchId: branchName,
                };
            }

            return { type: dataSyncProtocol.ResourceType.BRANCH, branchId: branchName };
        })();

        // Control frames must not reactivate the subscription they are closing or opening.
        this.encoder = createDataSyncEncoder(({ chunk }) => {
            if (this._webSocket.state === WebSocketState.OPEN) {
                this._webSocket.send(chunk);
            }
        });
    }

    public get webSocket(): WebSocket {
        return this._webSocket;
    }

    public get state(): WebSocketState {
        return this.internalState;
    }

    private set state(state: WebSocketState) {
        this.internalState = state;
        this.emitter.emit('state', state);
    }

    public isDestroyed(): boolean {
        return this.destroyed;
    }

    public open(): Promise<void> {
        if (this.state === WebSocketState.OPEN) {
            return Promise.resolve();
        }

        if (this.openPromise) {
            return this.openPromise;
        }

        if (this.destroyed) {
            return Promise.reject(new Error('Socket has been destroyed'));
        }

        const promise = (async () => {
            let lastError: unknown;
            let hasCapturedError = false;
            for (let attempt = 0; attempt <= MAX_OPEN_RETRIES; attempt++) {
                if (this.destroyed) {
                    // Destroyed externally (e.g. close()/destroy() was called).
                    // If we captured an error, propagate it; otherwise resolve silently.
                    if (hasCapturedError) {
                        throw lastError;
                    }
                    return;
                }
                try {
                    await this._doOpen();
                    return;
                } catch (error) {
                    lastError = error;
                    hasCapturedError = true;

                    // Retrying here only helps while the shared WebSocket is up, where
                    // re-sending the subscribe frame is the one thing that can recover this
                    // socket. When the transport is down the manager's reconnection backoff
                    // owns the retry and re-opens every socket once the connection is back;
                    // looping here would stack a much tighter retry loop on top of it, and
                    // re-resolve the socket URL (and its auth token) on each pass.
                    if (
                        this._webSocket.state !== WebSocketState.OPEN ||
                        attempt >= MAX_OPEN_RETRIES ||
                        this.destroyed
                    ) {
                        break;
                    }

                    logger.warn('Failed to open socket, retrying', {
                        error,
                        attempt,
                        maxRetries: MAX_OPEN_RETRIES,
                    });
                    await new Promise<void>((resolve) =>
                        setTimeout(resolve, OPEN_RETRY_DELAY_MS * (attempt + 1))
                    );
                }
            }
            throw lastError;
        })().finally(() => {
            // Only clear openPromise if it still points to this attempt. If close()/destroy()
            // reset it to undefined and a subsequent open() installed a new in-flight promise
            // before this attempt settled, clearing unconditionally would wipe out that newer
            // promise and allow yet another concurrent open() to start, reintroducing duplicate
            // subscribe frames.
            if (this.openPromise === promise) {
                this.openPromise = undefined;
            }
        });
        this.openPromise = promise;
        return this.openPromise;
    }

    private async _doOpen(): Promise<void> {
        // If being reopened after inactivity, reset the flag and restart the
        // associated branch socket (if it exists and is not already open).
        const wasInactive = this._inactive;
        this._inactive = false;
        if (wasInactive && this.identifier.type !== dataSyncProtocol.ResourceType.BRANCH) {
            void this.restartBranchSocket?.();
        }

        this.state = WebSocketState.CONNECTING;
        const subscriptionId = await this.getNewSubscriptionId();
        this.setupListeners(); // Ensure listeners are set up before opening the WebSocket to avoid missing any events
        // We check if the underlying WebSocket is already
        if (this._webSocket.state === WebSocketState.OPEN) {
            try {
                await this.sendSubscribe(subscriptionId);
                this.state = WebSocketState.OPEN;
                this.resetInactivityTimer();
            } catch (error) {
                // We avoid error state as much as possible as it just breaks the socket without a clear way to recover.
                this.state = WebSocketState.CLOSED;
                throw error;
            }
            return;
        }
        // Capture the WebSocket reference used for this attempt. The manager may replace
        // the underlying WebSocket object (via updateWebSocket) while ensureWebSocketOpen is
        // awaited; if that happens we throw a sentinel so the retry loop re-runs _doOpen()
        // against the fresh WebSocket (which will already be OPEN → fast path).
        const capturedWs = this._webSocket;

        // Create promise that resolves when this socket's state becomes OPEN
        // This listener is scoped to a single _doOpen() attempt and must be removed if the
        // manager-level open fails before the socket reaches a terminal state.
        let cleanupWaitForOpen: (() => void) | undefined;
        const waitForOpen = new Promise<void>((resolve, reject) => {
            let settled = false;
            const cleanup = () => {
                cleanupWaitForOpen?.();
                cleanupWaitForOpen = undefined;
            };

            cleanupWaitForOpen = capturedWs.listen('state', (state) => {
                if (settled) {
                    return;
                }

                if (state === WebSocketState.OPEN) {
                    settled = true;
                    cleanup();

                    void (async () => {
                        try {
                            await this.sendSubscribe(subscriptionId);
                            this.state = WebSocketState.OPEN;
                            this.resetInactivityTimer();
                            resolve();
                        } catch (error) {
                            this.state = WebSocketState.CLOSED;
                            reject(error);
                        }
                    })();
                } else if (state === WebSocketState.ERRORED) {
                    settled = true;
                    this.state = WebSocketState.CLOSED;
                    cleanup();
                    reject(new Error('WebSocket connection failed'));
                }
            });
        });

        // A failing open() moves the WebSocket to ERRORED synchronously, so the listener above
        // can reject while we are still awaiting ensureWebSocketOpen() below — and the error
        // paths that follow abandon this promise entirely. Both leave the rejection unobserved
        // and surface it as an unhandled rejection. Mark it handled here; the caller still gets
        // the rejection through the `return waitForOpen` at the end of this method.
        waitForOpen.catch(() => {});

        // Request the manager to ensure the WebSocket is opened
        // This is coordinated to only happen once even if multiple sockets request it
        try {
            await this.ensureWebSocketOpen();
        } catch (error) {
            cleanupWaitForOpen?.();
            cleanupWaitForOpen = undefined;
            throw error;
        }

        // If the manager replaced the WebSocket while we were waiting, abandon this attempt
        // and let the retry loop re-run _doOpen() with the new (already-open) WebSocket.
        if (this._webSocket !== capturedWs) {
            cleanupWaitForOpen?.();
            cleanupWaitForOpen = undefined;
            throw new Error('WebSocket was replaced, retrying with new connection');
        }

        // Wait for our state to be updated by the manager
        return waitForOpen;
    }

    public close(_state?: WebSocketState.CLOSED | WebSocketState.ERRORED): void {
        this.openPromise = undefined;
        this.state = WebSocketState.CLOSED;
        // Clean up listeners first to prevent send() from triggering a reconnection
        // during the unsubscribe frame send.
        this.cleanup();

        // It should remove the listener on the server side
        this.sendUnsubscribeFrame();

        // Remove from manager tracking (may close the shared WebSocket if last)
        this.onRemove();
    }

    public destroy(): void {
        this.openPromise = undefined;
        this.state = WebSocketState.CLOSED;
        this.destroyed = true;
        // Clean up listeners first to prevent send() from triggering a reconnection
        // during the unsubscribe frame send.
        this.cleanup();

        this.sendUnsubscribeFrame();

        // Remove from manager tracking (may close the shared WebSocket if last)
        this.onRemove();
    }

    public async forceScheduledReconnection(): Promise<void> {
        await this.forceReconnection();
    }

    public listen(event: 'state', callback: (state: WebSocketState) => void): () => void;
    public listen(event: 'state', callback: (event: any) => void): () => void {
        this.emitter.on(event, callback);
        return () => {
            this.emitter.off(event, callback);
        };
    }

    public send(message: WebSocketMessage): void {
        if (typeof message === 'string') {
            return;
        }

        const needsSubscription = this._inactive || this.state === WebSocketState.CONNECTING;
        if (needsSubscription || this._webSocket.state !== WebSocketState.OPEN) {
            // We don't queue message here as they will not be valid anymore
            // We'll resend a promptInit message on reconnect to ensure we have the correct state
            // Skip reconnection if we're in the process of closing/destroying
            if (this.destroyed) {
                return;
            }
            void (needsSubscription ? this.open() : this.ensureWebSocketOpen())
                .then(() => {
                    this.resetInactivityTimer(true);
                })
                .catch((error) => {
                    logger.error('Failed to reopen socket for sending message', {
                        error,
                    });
                });
            return;
        }

        this._webSocket.send(message);
        this.resetInactivityTimer();
    }

    public onMessage(message: dataSyncProtocol.Message) {
        this.resetInactivityTimer();
        if (message.promptInit) {
            return this.decoderHandlers.promptInit(message.promptInit);
        }
        if (message.init) {
            return this.decoderHandlers.init(message.init);
        }
        if (message.update) {
            return this.decoderHandlers.update(message.update);
        }
        if (message.updatePresence) {
            return this.decoderHandlers.updatePresence(message.updatePresence);
        }
        if (message.broadcastOperation) {
            return this.decoderHandlers.broadcastOperation(message.broadcastOperation);
        }
        if (message.broadcastPresences) {
            return this.decoderHandlers.broadcastPresences(message.broadcastPresences);
        }
        if (message.ack) {
            return this.decoderHandlers.ack(message.ack);
        }
        if (message.rpcRequest) {
            return this.decoderHandlers.rpcRequest(message.rpcRequest);
        }
        if (message.rpcReply) {
            // Emit subscribe-reply event if this is the subscribe reply
            this.emitter.emit('rpcReply', message.rpcReply);
            return this.decoderHandlers.rpcReply(message.rpcReply);
        }
        if (message.broadcastMarkers) {
            return this.decoderHandlers.broadcastMarkers(message.broadcastMarkers);
        }
    }

    public async reconnect() {
        this.state = WebSocketState.CLOSED;
        await this.open();
    }

    private async sendSubscribe(subscriptionId: number): Promise<void> {
        const messageId = this.encoder.newMessageID();
        // We need to set the subscriptionId before sending the subscribe frame, so that we can correctly route the subscribe reply to this socket's listeners.
        this.subscriptionId = subscriptionId;

        // Create a promise that resolves when we receive the subscribe reply
        const waitForReply = new Promise<void>((resolve, reject) => {
            let timeout: NodeJS.Timeout;
            let settled = false;
            const cleanup = () => {
                this.emitter.removeListener('rpcReply', callback);
                clearTimeout(timeout);
            };

            const callback = (reply: dataSyncProtocol.IRPCReplyMessage) => {
                if (settled || reply.replyTo !== messageId) {
                    return;
                }

                if (!reply.payload) {
                    settled = true;
                    cleanup();
                    reject(new Error('Subscribe failed: no payload in reply '));
                    return;
                }

                const message = dataSyncProtocol.SubscribeReply.decode(reply.payload);
                settled = true;
                cleanup();
                if (message.success) {
                    resolve();
                } else {
                    reject(new Error(`Subscribe failed: ${message || 'unknown error'}`));
                }
            };

            timeout = setTimeout(() => {
                if (settled) {
                    return;
                }
                settled = true;
                cleanup();
                reject(new Error('Subscribe RPC timeout'));
            }, 10000); // 10 second timeout

            this.emitter.on('rpcReply', callback);
        });

        this.encoder.write({
            rpcRequest: {
                id: messageId,
                service: 'Connection',
                method: 'subscribe',
                payload: dataSyncProtocol.SubscribeRequest.encode({
                    id: subscriptionId,
                    identifier: this.identifier,
                }).finish(),
                subscriptionId,
            },
        });

        // Wait for the reply
        await waitForReply;
    }

    private sendUnsubscribeFrame(): void {
        // Send unsubscribe via RPC
        const messageId = this.encoder.newMessageID();
        this.encoder.write({
            rpcRequest: {
                id: messageId,
                service: 'Connection',
                method: 'unsubscribe',
                payload: dataSyncProtocol.UnsubscribeRequest.encode({}).finish(),
                subscriptionId: this.subscriptionId,
            },
        });
    }

    /**
     * Replace the underlying WebSocket with a new one.
     * Called by the manager when the previous WebSocket entered an unrecoverable state
     * and a fresh connection object has been created. All persistent listeners are
     * migrated to the new WebSocket and the socket is marked as CLOSED so that the
     * next open() call (triggered by the new WebSocket's OPEN event) can proceed.
     */
    public updateWebSocket(ws: WebSocket): void {
        // Remove listeners from the old WebSocket before swapping.
        this.cleanup();

        this._webSocket = ws;

        // Clear any in-flight open promise so the next open() is a fresh attempt.
        this.openPromise = undefined;

        // Move the state to CLOSED so open() is not a no-op and the OPEN handler in
        // setupListeners() triggers correctly when the new WebSocket opens.
        if (this.internalState !== WebSocketState.CLOSED) {
            this.state = WebSocketState.CLOSED;
        }

        // Re-register persistent listeners on the new WebSocket.
        this.setupListeners();
    }

    /**
     * Force this socket to emit an error state.
     * Called by the manager when a connection timeout occurs.
     */
    public forceErrorState(): void {
        this.state = WebSocketState.ERRORED;
    }

    /**
     * Clean up event listeners.
     */
    private cleanup(): void {
        // Cancel the inactivity timer
        this.resetInactivityTimer.cancel();

        // Remove message listener
        if (this.unlistenMessages) {
            this.unlistenMessages();
            this.unlistenMessages = undefined;
        }

        // Remove WebSocket state listener
        if (this.unlistenWebSocketState) {
            this.unlistenWebSocketState();
            this.unlistenWebSocketState = undefined;
        }
    }

    // Set up listeners for WebSocket state changes and incoming messages
    private setupListeners(): void {
        if (!this.unlistenWebSocketState) {
            // Listen to WebSocket state changes
            this.unlistenWebSocketState = this._webSocket.listen(
                'state',
                (state: WebSocketState) => {
                    // Update internal state based on underlying websocket
                    if (state === WebSocketState.CLOSED || state === WebSocketState.ERRORED) {
                        // The V2Socket should never be in ERRORED state, as it prevents the application from recovering.
                        // This is expected for Hive V1 as the socket is directly tied to the WebSocket connection, but for V2 we want to be more resilient and just mark it as CLOSED so that the manager can attempt to reopen it.
                        // The V2Manager is the one handling the reconnection logic and we can create a new websocket and associate it to the existing V2Sockets
                        this.state = WebSocketState.CLOSED;
                    } else if (state === WebSocketState.OPEN) {
                        if (
                            this.state === WebSocketState.CLOSED ||
                            this.state === WebSocketState.ERRORED
                        ) {
                            this.open().catch((error) => {
                                logger.error(
                                    'Failed to reopen socket after WebSocket reconnected',
                                    {
                                        error,
                                    }
                                );
                            });
                        }
                    }
                }
            );
        }

        if (!this.unlistenMessages) {
            // Listen to messages from the manager's emitter and filter by identifier
            this.unlistenMessages = (() => {
                const messageHandler = ({
                    message,
                    ws,
                }: {
                    message: dataSyncProtocol.Message;
                    ws: WebSocket;
                }) => {
                    // Only process messages for this socket's WebSocket
                    if (ws !== this._webSocket) {
                        return;
                    }

                    const identifier = getSubscriptionId(message);
                    if (!identifier) {
                        return;
                    }

                    // Check if the message is for this socket
                    if (identifier === this.subscriptionId) {
                        this.onMessage(message);
                    }
                };

                this.managerEmitter.on('message', messageHandler);

                return () => {
                    this.managerEmitter.off('message', messageHandler);
                };
            })();
        }
    }
}
